import { useState, useEffect, useCallback, useRef } from 'react'
import { cmsApi, type ApiCollection, type ApiCollectionItem } from '../utils/api'

type FieldDef = { name: string; type: string; required?: boolean; options?: string[]; relatedCollection?: string }

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '10px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
}

function buildInitialData(schema: FieldDef[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of schema) {
    if (f.type === 'number') out[f.name] = 0
    else if (f.type === 'boolean') out[f.name] = false
    else out[f.name] = ''
  }
  return out
}

const RTB_STYLE: React.CSSProperties = {
  padding: '3px 7px', background: 'var(--bg-3)', border: '1px solid var(--border)',
  borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 700, lineHeight: 1.4,
  color: 'var(--text-secondary)',
}

function RichTextEditor({ label, value, onChange }: { label: string; value: string; onChange: (v: unknown) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const skipUpdate = useRef(false)

  useEffect(() => {
    if (ref.current && !skipUpdate.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value
    }
    skipUpdate.current = false
  }, [value])

  function exec(cmd: string, val?: string) {
    ref.current?.focus()
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    document.execCommand(cmd, false, val)
    skipUpdate.current = true
    onChange(ref.current?.innerHTML ?? '')
  }

  function handleInput() {
    skipUpdate.current = true
    onChange(ref.current?.innerHTML ?? '')
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Tab') { e.preventDefault(); exec('insertText', '    ') }
  }

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-1)' }}>
        <div style={{ display: 'flex', gap: '3px', padding: '5px 7px', borderBottom: '1px solid var(--border)', background: 'var(--bg-2)', flexWrap: 'wrap' }}>
          <button type="button" style={RTB_STYLE} onClick={() => exec('bold')} title="Жирный"><b>B</b></button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('italic')} title="Курсив"><i>I</i></button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('underline')} title="Подчёркнутый"><u>U</u></button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('strikeThrough')} title="Зачёркнутый"><s>S</s></button>
          <div style={{ width: '1px', background: 'var(--border)', margin: '0 2px' }} />
          <button type="button" style={RTB_STYLE} onClick={() => exec('formatBlock', 'h2')} title="Заголовок H2">H2</button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('formatBlock', 'h3')} title="Заголовок H3">H3</button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('formatBlock', 'p')} title="Параграф">P</button>
          <div style={{ width: '1px', background: 'var(--border)', margin: '0 2px' }} />
          <button type="button" style={RTB_STYLE} onClick={() => exec('insertUnorderedList')} title="Список">•</button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('insertOrderedList')} title="Нумерованный список">1.</button>
          <div style={{ width: '1px', background: 'var(--border)', margin: '0 2px' }} />
          <button type="button" style={RTB_STYLE} onClick={() => {
            const url = prompt('URL ссылки:')
            if (url) exec('createLink', url)
          }} title="Ссылка">🔗</button>
          <button type="button" style={RTB_STYLE} onClick={() => exec('unlink')} title="Убрать ссылку">✂</button>
          <div style={{ width: '1px', background: 'var(--border)', margin: '0 2px' }} />
          <button type="button" style={RTB_STYLE} onClick={() => exec('removeFormat')} title="Очистить форматирование">✕</button>
        </div>
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          style={{
            minHeight: '120px', padding: '10px 12px', outline: 'none',
            fontSize: '13px', lineHeight: 1.7, color: 'var(--text-primary)',
          }}
        />
      </div>
    </div>
  )
}

function RelationFieldEditor({ field, value, onChange, siteId }: { field: FieldDef; value: unknown; onChange: (v: unknown) => void; siteId: string }) {
  const [items, setItems] = useState<Array<{ id: string; label: string }>>([])
  useEffect(() => {
    if (!field.relatedCollection) return
    // Find collection by slug, then load its items
    cmsApi.listCollections(siteId).then((cols: ApiCollection[]) => {
      const col = cols.find(c => c.slug === field.relatedCollection)
      if (!col) return
      cmsApi.listItems(siteId, col.id).then((its: ApiCollectionItem[]) => {
        setItems(its.map(it => {
          const d = it.data as Record<string, unknown>
          const label = (d.title ?? d.name ?? d.slug ?? it.id) as string
          return { id: it.id, label: String(label) }
        }))
      }).catch(() => null)
    }).catch(() => null)
  }, [field.relatedCollection, siteId])

  return (
    <div>
      <label style={labelStyle}>{field.name} <span style={{ opacity: 0.6, fontWeight: 400 }}>(→ {field.relatedCollection})</span></label>
      <select className="prop-select" value={value == null ? '' : String(value)} onChange={e => onChange(e.target.value || null)}>
        <option value="">— не выбрано —</option>
        {items.map(it => <option key={it.id} value={it.id}>{it.label}</option>)}
      </select>
    </div>
  )
}

function FieldEditor({ field, value, onChange, siteId }: { field: FieldDef; value: unknown; onChange: (v: unknown) => void; siteId?: string }) {
  const strVal = value == null ? '' : String(value)
  switch (field.type) {
    case 'richtext':
      return <RichTextEditor label={field.name} value={strVal} onChange={onChange} />
    case 'number':
      return (
        <div>
          <label style={labelStyle}>{field.name}</label>
          <input type="number" className="prop-input" value={strVal} onChange={e => onChange(Number(e.target.value))} />
        </div>
      )
    case 'boolean':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
          <input type="checkbox" checked={Boolean(value)} onChange={e => onChange(e.target.checked)} style={{ width: '14px', height: '14px' }} />
          {field.name}
        </label>
      )
    case 'date':
      return (
        <div>
          <label style={labelStyle}>{field.name}</label>
          <input type="date" className="prop-input" value={strVal} onChange={e => onChange(e.target.value)} />
        </div>
      )
    case 'image':
      return (
        <div>
          <label style={labelStyle}>{field.name} (URL)</label>
          <input type="url" className="prop-input" placeholder="https://..." value={strVal} onChange={e => onChange(e.target.value)} />
          {strVal && <img src={strVal} alt="" style={{ marginTop: '6px', maxWidth: '100%', maxHeight: '80px', objectFit: 'contain', borderRadius: '6px', border: '1px solid var(--border)' }} />}
        </div>
      )
    case 'select':
      return (
        <div>
          <label style={labelStyle}>{field.name}</label>
          <select className="prop-select" value={strVal} onChange={e => onChange(e.target.value)}>
            <option value="">— Выберите —</option>
            {(field.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      )
    case 'relation':
      return siteId
        ? <RelationFieldEditor field={field} value={value} onChange={onChange} siteId={siteId} />
        : <div><label style={labelStyle}>{field.name}</label><input type="text" className="prop-input" value={strVal} onChange={e => onChange(e.target.value)} /></div>
    default:
      return (
        <div>
          <label style={labelStyle}>{field.name}</label>
          <input type="text" className="prop-input" value={strVal} onChange={e => onChange(e.target.value)} />
        </div>
      )
  }
}

function ItemForm({
  schema,
  data,
  onChange,
  onSave,
  onCancel,
  saving,
  label,
  siteId,
}: {
  schema: FieldDef[]
  data: Record<string, unknown>
  onChange: (k: string, v: unknown) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
  label: string
  siteId?: string
}) {
  const [showJson, setShowJson] = useState(false)
  const [jsonText, setJsonText] = useState(JSON.stringify(data, null, 2))
  const [jsonErr, setJsonErr] = useState('')

  useEffect(() => { setJsonText(JSON.stringify(data, null, 2)) }, [data])

  function applyJson() {
    try {
      const parsed = JSON.parse(jsonText) as Record<string, unknown>
      for (const k of Object.keys(parsed)) onChange(k, parsed[k])
      setJsonErr('')
      setShowJson(false)
    } catch {
      setJsonErr('Неверный JSON')
    }
  }

  const hasSchema = schema.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {hasSchema && !showJson && schema.map(f => (
        <FieldEditor key={f.name} field={f} value={data[f.name]} onChange={v => onChange(f.name, v)} siteId={siteId} />
      ))}
      {(!hasSchema || showJson) && (
        <div>
          <label style={labelStyle}>JSON</label>
          <textarea
            rows={10}
            className="prop-textarea"
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
            value={jsonText}
            onChange={e => { setJsonText(e.target.value); setJsonErr('') }}
          />
          {jsonErr && <div style={{ color: '#f87171', fontSize: '11px', marginTop: '4px' }}>{jsonErr}</div>}
          {hasSchema && (
            <button onClick={applyJson} style={{ ...cancelBtnStyle, marginTop: '4px', fontSize: '11px', width: '100%' }}>Применить JSON</button>
          )}
        </div>
      )}
      {hasSchema && (
        <button
          onClick={() => setShowJson(v => !v)}
          style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
        >
          {showJson ? '← Форма' : 'Показать JSON'}
        </button>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button onClick={onSave} disabled={saving} style={saveBtnStyle}>{saving ? '...' : label}</button>
        <button onClick={onCancel} style={cancelBtnStyle}>Отмена</button>
      </div>
    </div>
  )
}

interface Props {
  siteId: string | null
  onClose: () => void
}

type View = 'collections' | 'items'

export function CmsPanel({ siteId, onClose }: Props) {
  const [view, setView] = useState<View>('collections')
  const [collections, setCollections] = useState<ApiCollection[]>([])
  const [activeCollection, setActiveCollection] = useState<ApiCollection | null>(null)
  const [items, setItems] = useState<ApiCollectionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // New collection form
  const [newColName, setNewColName] = useState('')
  const [newColSlug, setNewColSlug] = useState('')
  const [newColSchema, setNewColSchema] = useState<FieldDef[]>([])
  const [newFieldName, setNewFieldName] = useState('')
  const [newFieldType, setNewFieldType] = useState('text')
  const [newFieldRelCol, setNewFieldRelCol] = useState('')
  const [creatingCol, setCreatingCol] = useState(false)
  const [showNewCol, setShowNewCol] = useState(false)
  const [editingSchema, setEditingSchema] = useState(false)

  // Item editor
  const [editingItem, setEditingItem] = useState<ApiCollectionItem | null>(null)
  const [editData, setEditData] = useState<Record<string, unknown>>({})
  const [savingItem, setSavingItem] = useState(false)
  const [showNewItem, setShowNewItem] = useState(false)
  const [newItemData, setNewItemData] = useState<Record<string, unknown>>({})
  const [importing, setImporting] = useState(false)
  const csvInputRef = useRef<HTMLInputElement>(null)

  const loadCollections = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const data = await cmsApi.listCollections(siteId)
      setCollections(data)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [siteId])

  const loadItems = useCallback(async () => {
    if (!siteId || !activeCollection) return
    setLoading(true)
    try {
      const data = await cmsApi.listItems(siteId, activeCollection.id)
      setItems(data)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [siteId, activeCollection])

  useEffect(() => { loadCollections() }, [loadCollections])
  useEffect(() => { if (activeCollection) loadItems() }, [activeCollection, loadItems])

  async function createCollection() {
    if (!siteId || !newColName.trim()) return
    setCreatingCol(true)
    try {
      const slug = newColSlug.trim() || newColName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      const col = await cmsApi.createCollection(siteId, newColName.trim(), slug, newColSchema)
      setCollections(c => [...c, col])
      setNewColName('')
      setNewColSlug('')
      setNewColSchema([])
      setNewFieldName('')
      setShowNewCol(false)
    } catch (e) {
      setError(String(e))
    } finally {
      setCreatingCol(false)
    }
  }

  function addSchemaField() {
    const name = newFieldName.trim().replace(/\s+/g, '_').toLowerCase()
    if (!name || newColSchema.some(f => f.name === name)) return
    const field: FieldDef = { name, type: newFieldType }
    if (newFieldType === 'relation' && newFieldRelCol.trim()) field.relatedCollection = newFieldRelCol.trim()
    setNewColSchema(s => [...s, field])
    setNewFieldName('')
    setNewFieldType('text')
    setNewFieldRelCol('')
  }

  async function saveCollectionSchema() {
    if (!siteId || !activeCollection) return
    try {
      const updated = await cmsApi.updateCollection(siteId, activeCollection.id, activeCollection.schema)
      setCollections(cols => cols.map(c => c.id === updated.id ? updated : c))
      setActiveCollection(updated)
      setEditingSchema(false)
    } catch (e) {
      setError(String(e))
    }
  }

  async function deleteCollection(id: string) {
    if (!siteId || !confirm('Удалить коллекцию и все её записи?')) return
    try {
      await cmsApi.deleteCollection(siteId, id)
      setCollections(c => c.filter(x => x.id !== id))
      if (activeCollection?.id === id) {
        setActiveCollection(null)
        setView('collections')
      }
    } catch (e) {
      setError(String(e))
    }
  }

  function openCollection(col: ApiCollection) {
    setActiveCollection(col)
    setItems([])
    setView('items')
    setShowNewItem(false)
    setEditingItem(null)
  }

  function startEdit(item: ApiCollectionItem) {
    setEditingItem(item)
    setEditData({ ...item.data })
    setShowNewItem(false)
  }

  async function saveItem() {
    if (!siteId || !activeCollection || !editingItem) return
    setSavingItem(true)
    try {
      const updated = await cmsApi.updateItem(siteId, activeCollection.id, editingItem.id, editData, editingItem.published)
      setItems(items => items.map(i => i.id === updated.id ? updated : i))
      setEditingItem(updated)
      setEditData({ ...updated.data })
    } catch (e) {
      setError(String(e))
    } finally {
      setSavingItem(false)
    }
  }

  async function togglePublished(item: ApiCollectionItem) {
    if (!siteId || !activeCollection) return
    const updated = await cmsApi.updateItem(siteId, activeCollection.id, item.id, item.data, !item.published)
    setItems(items => items.map(i => i.id === updated.id ? updated : i))
    if (editingItem?.id === item.id) setEditingItem(updated)
  }

  async function deleteItem(id: string) {
    if (!siteId || !activeCollection) return
    try {
      await cmsApi.deleteItem(siteId, activeCollection.id, id)
      setItems(i => i.filter(x => x.id !== id))
      if (editingItem?.id === id) setEditingItem(null)
    } catch (e) {
      setError(String(e))
    }
  }

  async function createItem() {
    if (!siteId || !activeCollection) return
    setSavingItem(true)
    try {
      const item = await cmsApi.createItem(siteId, activeCollection.id, newItemData)
      setItems(i => [item, ...i])
      setShowNewItem(false)
      setNewItemData(buildInitialData(activeCollection.schema))
    } catch (e) {
      setError(String(e))
    } finally {
      setSavingItem(false)
    }
  }

  function parseCsv(text: string): Record<string, string>[] {
    const lines = text.trim().split(/\r?\n/)
    if (lines.length < 2) return []
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''))
    return lines.slice(1).map(line => {
      const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g) ?? line.split(',')
      const row: Record<string, string> = {}
      headers.forEach((h, i) => { row[h] = (vals[i] ?? '').trim().replace(/^"|"$/g, '') })
      return row
    })
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    if (!siteId || !activeCollection) return
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    try {
      const text = await file.text()
      const rows = parseCsv(text)
      if (rows.length === 0) { setError('CSV пустой или неверный формат'); return }
      const result = await cmsApi.bulkImport(siteId, activeCollection.id, rows)
      await loadItems()
      setError('')
      alert(`✅ Импортировано ${result.created} записей`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка импорта')
    } finally {
      setImporting(false)
      if (csvInputRef.current) csvInputRef.current.value = ''
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '900px', maxWidth: '96vw', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {view === 'items' && (
            <button
              onClick={() => { setView('collections'); setEditingItem(null) }}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: 1 }}
            >←</button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {view === 'collections' ? 'CMS — Коллекции' : activeCollection?.name}
            </div>
            {view === 'items' && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                <code style={{ fontSize: '11px' }}>/api/public/{'{siteSlug}'}/collections/{activeCollection?.slug}/items</code>
              </div>
            )}
          </div>
          {view === 'collections' && (
            <button
              onClick={() => setShowNewCol(v => !v)}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >
              + Коллекция
            </button>
          )}
          {view === 'items' && (
            <>
              <label
                title="Импорт из CSV"
                style={{ padding: '6px 12px', background: importing ? 'var(--bg-3)' : 'var(--bg-2)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: importing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {importing ? '⏳' : '📥'} CSV
                <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvImport} />
              </label>
              <button
                onClick={() => { setShowNewItem(v => !v); setEditingItem(null); setNewItemData(buildInitialData(activeCollection?.schema ?? [])) }}
                style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                + Запись
              </button>
            </>
          )}
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {error && (
          <div style={{ padding: '8px 20px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', flexShrink: 0 }}>
            {error}
            <button onClick={() => setError('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>×</button>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

          {/* Collections list */}
          {view === 'collections' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {showNewCol && (
                <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={labelStyle}>Название</label>
                    <input
                      autoFocus
                      className="prop-input"
                      placeholder="Blog Posts"
                      value={newColName}
                      onChange={e => {
                        setNewColName(e.target.value)
                        if (!newColSlug) setNewColSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
                      }}
                      onKeyDown={e => e.key === 'Enter' && createCollection()}
                    />
                  </div>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelStyle}>Slug (для API)</label>
                    <input
                      className="prop-input"
                      placeholder="blog-posts"
                      value={newColSlug}
                      onChange={e => setNewColSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    />
                  </div>
                  {/* Schema fields */}
                  <div style={{ flex: '1 1 100%' }}>
                    <label style={labelStyle}>Поля схемы</label>
                    {newColSchema.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <code style={{ fontSize: '12px', flex: 1, padding: '4px 8px', background: 'var(--bg-3)', borderRadius: '5px', color: 'var(--accent)' }}>{f.name}</code>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.type}</span>
                        <button onClick={() => setNewColSchema(s => s.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <input
                        className="prop-input"
                        placeholder="field_name"
                        value={newFieldName}
                        onChange={e => setNewFieldName(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))}
                        onKeyDown={e => e.key === 'Enter' && addSchemaField()}
                        style={{ flex: 1 }}
                      />
                      <select
                        className="prop-select"
                        value={newFieldType}
                        onChange={e => { setNewFieldType(e.target.value); setNewFieldRelCol('') }}
                        style={{ width: '90px' }}
                      >
                        {['text','richtext','number','boolean','date','image','select','relation'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button onClick={addSchemaField} style={{ padding: '6px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Поле</button>
                    </div>
                    {newFieldType === 'relation' && (
                      <input
                        className="prop-input"
                        placeholder="Slug коллекции (напр. blog-posts)"
                        value={newFieldRelCol}
                        onChange={e => setNewFieldRelCol(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        style={{ marginTop: '4px' }}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flex: '1 1 100%' }}>
                    <button
                      onClick={createCollection}
                      disabled={creatingCol || !newColName.trim()}
                      style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {creatingCol ? '...' : 'Создать'}
                    </button>
                    <button
                      onClick={() => setShowNewCol(false)}
                      style={{ padding: '8px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >Отмена</button>
                  </div>
                </div>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>Загрузка...</div>
              ) : collections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>📂</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Нет коллекций. Создайте первую!</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {collections.map(col => (
                    <div
                      key={col.id}
                      onClick={() => openCollection(col)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
                      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      <div style={{ fontSize: '20px' }}>📋</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{col.name}</div>
                        <code style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{col.slug}</code>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); deleteCollection(col.id) }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', opacity: 0.6 }}
                        title="Удалить коллекцию"
                      >🗑</button>
                      <span style={{ fontSize: '18px', color: 'var(--text-muted)' }}>›</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Items list */}
          {view === 'items' && (
            <div style={{ flex: 1, overflowY: 'auto', borderRight: editingItem || showNewItem ? '1px solid var(--border)' : 'none', padding: '12px' }}>
              {/* Schema summary */}
              {activeCollection && (
                <div style={{ marginBottom: '10px', padding: '8px 10px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Поля:</span>
                    {activeCollection.schema.length === 0
                      ? <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>не определены</span>
                      : activeCollection.schema.map(f => (
                        <span key={f.name} style={{ fontSize: '11px', padding: '2px 7px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                          {f.name} <span style={{ color: 'var(--text-muted)' }}>({f.type})</span>
                        </span>
                      ))
                    }
                    <button
                      onClick={() => setEditingSchema(v => !v)}
                      style={{ marginLeft: 'auto', fontSize: '10px', padding: '2px 8px', background: editingSchema ? 'var(--accent)' : 'var(--bg-3)', color: editingSchema ? '#fff' : 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      {editingSchema ? 'Скрыть' : '✏ Схему'}
                    </button>
                  </div>
                  {editingSchema && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                      {activeCollection.schema.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          <code style={{ fontSize: '12px', flex: 1, padding: '3px 6px', background: 'var(--bg-3)', borderRadius: '4px', color: 'var(--accent)' }}>{f.name}</code>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '60px' }}>{f.type}</span>
                          <button
                            onClick={() => {
                              const newSchema = activeCollection.schema.filter((_, j) => j !== i)
                              setActiveCollection({ ...activeCollection, schema: newSchema })
                            }}
                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}
                          >×</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                        <input
                          className="prop-input"
                          placeholder="field_name"
                          value={newFieldName}
                          onChange={e => setNewFieldName(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newFieldName.trim() && activeCollection) {
                              const field: FieldDef = { name: newFieldName.trim(), type: newFieldType }
                              if (newFieldType === 'relation' && newFieldRelCol.trim()) field.relatedCollection = newFieldRelCol.trim()
                              setActiveCollection({ ...activeCollection, schema: [...activeCollection.schema, field] })
                              setNewFieldName('')
                              setNewFieldRelCol('')
                            }
                          }}
                          style={{ flex: 1 }}
                        />
                        <select
                          className="prop-select"
                          value={newFieldType}
                          onChange={e => { setNewFieldType(e.target.value); setNewFieldRelCol('') }}
                          style={{ width: '90px' }}
                        >
                          {['text','richtext','number','boolean','date','image','select','relation'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button
                          onClick={() => {
                            if (!newFieldName.trim() || !activeCollection) return
                            const field: FieldDef = { name: newFieldName.trim(), type: newFieldType }
                            if (newFieldType === 'relation' && newFieldRelCol.trim()) field.relatedCollection = newFieldRelCol.trim()
                            setActiveCollection({ ...activeCollection, schema: [...activeCollection.schema, field] })
                            setNewFieldName('')
                            setNewFieldRelCol('')
                          }}
                          style={{ padding: '6px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}
                        >+ Поле</button>
                      </div>
                      {newFieldType === 'relation' && (
                        <input
                          className="prop-input"
                          placeholder="Slug коллекции (напр. blog-posts)"
                          value={newFieldRelCol}
                          onChange={e => setNewFieldRelCol(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          style={{ marginTop: '4px' }}
                        />
                      )}
                      <button onClick={saveCollectionSchema} style={{ ...saveBtnStyle, marginTop: '8px', fontSize: '12px' }}>Сохранить схему</button>
                    </div>
                  )}
                </div>
              )}
              {showNewItem && (
                <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '10px' }}>Новая запись</div>
                  <ItemForm
                    schema={activeCollection?.schema ?? []}
                    data={newItemData}
                    onChange={(k, v) => setNewItemData(d => ({ ...d, [k]: v }))}
                    onSave={createItem}
                    onCancel={() => setShowNewItem(false)}
                    saving={savingItem}
                    label="Создать"
                    siteId={siteId ?? undefined}
                  />
                </div>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>Загрузка...</div>
              ) : items.length === 0 && !showNewItem ? (
                <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📄</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Нет записей</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {items.map(item => (
                    <div
                      key={item.id}
                      onClick={() => startEdit(item)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                        background: editingItem?.id === item.id ? 'rgba(91,106,240,0.08)' : 'var(--bg-2)',
                        border: `1px solid ${editingItem?.id === item.id ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: '8px', cursor: 'pointer',
                      }}
                    >
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {Object.values(item.data).find(v => typeof v === 'string') as string || item.id.slice(0, 8)}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>
                          {new Date(item.updatedAt).toLocaleDateString('ru')}
                        </div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); togglePublished(item) }}
                        style={{
                          fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                          background: item.published ? '#dcfce7' : 'var(--bg-3)',
                          color: item.published ? '#15803d' : 'var(--text-muted)',
                        }}
                        title={item.published ? 'Снять с публикации' : 'Опубликовать'}
                      >
                        {item.published ? '✓' : '○'}
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); deleteItem(item.id) }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '2px 6px', opacity: 0.7 }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Item editor pane */}
          {view === 'items' && editingItem && !showNewItem && (
            <div style={{ width: '340px', overflowY: 'auto', padding: '16px', flexShrink: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '10px' }}>Редактирование записи</div>
              <ItemForm
                schema={activeCollection?.schema ?? []}
                data={editData}
                onChange={(k, v) => setEditData(d => ({ ...d, [k]: v }))}
                onSave={saveItem}
                onCancel={() => setEditingItem(null)}
                saving={savingItem}
                label="Сохранить"
                siteId={siteId ?? undefined}
              />
              <div style={{ marginTop: '14px', padding: '10px', background: 'var(--bg-2)', borderRadius: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Публичный API</div>
                <code style={{ wordBreak: 'break-all' }}>GET /api/public/{'{siteSlug}'}/collections/{activeCollection?.slug}/items</code>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


const saveBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: '7px',
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
}

const cancelBtnStyle: React.CSSProperties = {
  padding: '7px 12px',
  background: 'var(--bg-3)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '13px',
  color: 'var(--text-muted)',
  cursor: 'pointer',
}
